import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Menu } from 'components/atoms/dropdown';
import {
  DateFieldAbsoluteFilter,
  DateFieldFilter,
  DateFieldFilterOperationType,
  DateFieldRelativeFilter,
  RelativeDirection,
} from '../../filters/date';
import { renderAbsoluteMenuItem } from './absolute-menu-item';
import { isRelative, isBasic, isAbsolute, isOptional } from './helpers';
import { renderMenuItem } from './menu-item';
import { renderRelativeMenuItem } from './relative-menu-item';

const ItemGroup = styled(Menu.ItemGroup)`
  .ant-dropdown-menu-item-group-title {
    text-transform: capitalize;
    opacity: 0.67;
  }
`;

export const renderOverlay = (
  filter: DateFieldFilter,
  onChange: (filter: DateFieldFilter) => void,
  setVisible: (visible: boolean) => void,
  items: DateFieldFilter[],
  onAdvancedClick: () => void,
  advanced: boolean,
  getOperationOptionRenderer?: (
    type: DateFieldFilterOperationType,
    filter?: DateFieldFilter,
  ) => null | JSX.Element,
) => {
  const basicDates = items.filter((eachFilter) => isBasic(eachFilter.type));
  const relativeDates = items.filter((eachFilter) =>
    isRelative(eachFilter.type),
  );
  const absoluteDates = items.filter((eachFilter) =>
    isAbsolute(eachFilter.type),
  );
  const optionalDates = items.filter((eachFilter) =>
    isOptional(eachFilter.type),
  );

  return (
    <Menu
      className="date-criteria"
      selectable
      selectedKeys={[filter.type.toString()]}
      onSelect={({ key }) => {
        if (key === 'advanced') {
          window.setTimeout(onAdvancedClick, 30);

          return;
        }
        const type = key as DateFieldFilterOperationType;

        onChange({
          ...filter,
          type,
          days: (filter as DateFieldRelativeFilter).days,
          direction:
            (filter as DateFieldRelativeFilter).direction ??
            RelativeDirection.Ago,
          value: (filter as DateFieldAbsoluteFilter).value,
        } as DateFieldFilter);
        if (!isRelative(type) && !isAbsolute(type)) {
          setVisible(false);
        }
      }}
    >
      {basicDates.length ? (
        <>
          {basicDates.map((x) =>
            renderMenuItem(x.type, getOperationOptionRenderer),
          )}
          <Menu.Divider />
        </>
      ) : null}
      {!advanced && (
        <Menu.Item key="advanced" className="date-criteria">
          <FormattedMessage id="task_list.filters.advanced" />
        </Menu.Item>
      )}
      {advanced && (
        <>
          {relativeDates.length ? (
            <>
              <ItemGroup
                title={<FormattedMessage id="task_list.filters.relative" />}
              >
                {relativeDates.map((x) =>
                  renderRelativeMenuItem(
                    filter,
                    x.type,
                    onChange,
                    getOperationOptionRenderer,
                  ),
                )}
              </ItemGroup>
              <Menu.Divider />
            </>
          ) : null}

          {absoluteDates.length ? (
            <>
              <ItemGroup
                title={<FormattedMessage id="task_list.filters.absolute" />}
              >
                {absoluteDates.map((x) =>
                  renderAbsoluteMenuItem(
                    filter,
                    x.type,
                    onChange,
                    getOperationOptionRenderer,
                  ),
                )}
              </ItemGroup>
              <Menu.Divider />
            </>
          ) : null}

          {!filter.field.metaData?.required && optionalDates.length ? (
            <>
              {optionalDates.map((x) =>
                renderMenuItem(x.type, getOperationOptionRenderer),
              )}
            </>
          ) : null}
        </>
      )}
    </Menu>
  );
};
